import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Alert,
  Tabs,
  Tab,
  Grid,
  Divider,
} from "@mui/material";
import {
  History as HistoryIcon,
  Sports as SportsIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { Player, PlayerNumberAssignment, GameScore } from "../types";

interface HistoricalMatch {
  roundNumber: number;
  courtNumber: number;
  pair1: { player1: number; player2: number };
  pair2: { player1: number; player2: number };
  score: GameScore;
  maxGames: number;
}

interface HistoricalLeagueDayDialogProps {
  open: boolean;
  players: Player[];
  onClose: () => void;
  onSave: (data: {
    date: Date;
    courtCount: number;
    playerAssignments: PlayerNumberAssignment[];
    matches: HistoricalMatch[];
  }) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

export default function HistoricalLeagueDayDialog({
  open,
  players,
  onClose,
  onSave,
}: HistoricalLeagueDayDialogProps) {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [playerCount, setPlayerCount] = useState<4 | 8 | 12 | 16 | 20>(12);
  const [playerAssignments, setPlayerAssignments] = useState<
    PlayerNumberAssignment[]
  >(
    Array.from({ length: 12 }, (_, i) => ({
      playerId: "",
      playerName: "",
      assignedNumber: i + 1,
    }))
  );
  const [currentRound, setCurrentRound] = useState(0);
  const [matches, setMatches] = useState<HistoricalMatch[]>([]);
  const [error, setError] = useState("");
  const [editablePairings, setEditablePairings] = useState(true);

  // Calculate max rounds based on court count (1 court = 3 rounds, others = 4)
  const courtsPerRound = playerCount / 4;
  const maxRounds = courtsPerRound === 1 ? 3 : 4;

  // Generate match structure based on player count and pattern
  // Uses the same logic as the server's generateMatchPattern function
  const generateMatches = (): HistoricalMatch[] => {
    const courtsPerRound = playerCount / 4; // 8 players = 2 courts, 12 = 3, 16 = 4, 20 = 5

    let pattern: any[] = [];

    switch (courtsPerRound) {
      case 1: // 4 players, 1 court, 3 rounds
        pattern = [
          {
            roundNumber: 1,
            courts: [
              {
                courtNumber: 1,
                pair1: { player1: 1, player2: 2 },
                pair2: { player1: 3, player2: 4 },
              },
            ],
          },
          {
            roundNumber: 2,
            courts: [
              {
                courtNumber: 1,
                pair1: { player1: 1, player2: 3 },
                pair2: { player1: 2, player2: 4 },
              },
            ],
          },
          {
            roundNumber: 3,
            courts: [
              {
                courtNumber: 1,
                pair1: { player1: 1, player2: 4 },
                pair2: { player1: 2, player2: 3 },
              },
            ],
          },
        ];
        break;

      case 2: // 8 players, 2 courts
        pattern = [
          // Round 1
          {
            roundNumber: 1,
            courts: [
              {
                courtNumber: 1,
                pair1: { player1: 1, player2: 2 },
                pair2: { player1: 3, player2: 4 },
              },
              {
                courtNumber: 2,
                pair1: { player1: 5, player2: 6 },
                pair2: { player1: 7, player2: 8 },
              },
            ],
          },
          // Round 2
          {
            roundNumber: 2,
            courts: [
              {
                courtNumber: 1,
                pair1: { player1: 1, player2: 5 },
                pair2: { player1: 2, player2: 6 },
              },
              {
                courtNumber: 2,
                pair1: { player1: 3, player2: 7 },
                pair2: { player1: 4, player2: 8 },
              },
            ],
          },
          // Round 3
          {
            roundNumber: 3,
            courts: [
              {
                courtNumber: 1,
                pair1: { player1: 1, player2: 3 },
                pair2: { player1: 5, player2: 7 },
              },
              {
                courtNumber: 2,
                pair1: { player1: 2, player2: 4 },
                pair2: { player1: 6, player2: 8 },
              },
            ],
          },
          // Round 4
          {
            roundNumber: 4,
            courts: [
              {
                courtNumber: 1,
                pair1: { player1: 1, player2: 7 },
                pair2: { player1: 4, player2: 6 },
              },
              {
                courtNumber: 2,
                pair1: { player1: 2, player2: 8 },
                pair2: { player1: 3, player2: 5 },
              },
            ],
          },
        ];
        break;

      case 3: // 12 players, 3 courts
        pattern = [
          // Round 1
          {
            roundNumber: 1,
            courts: [
              {
                courtNumber: 1,
                pair1: { player1: 1, player2: 4 },
                pair2: { player1: 2, player2: 7 },
              },
              {
                courtNumber: 2,
                pair1: { player1: 3, player2: 10 },
                pair2: { player1: 5, player2: 12 },
              },
              {
                courtNumber: 3,
                pair1: { player1: 6, player2: 8 },
                pair2: { player1: 9, player2: 11 },
              },
            ],
          },
          // Round 2
          {
            roundNumber: 2,
            courts: [
              {
                courtNumber: 1,
                pair1: { player1: 1, player2: 2 },
                pair2: { player1: 3, player2: 12 },
              },
              {
                courtNumber: 2,
                pair1: { player1: 4, player2: 5 },
                pair2: { player1: 6, player2: 7 },
              },
              {
                courtNumber: 3,
                pair1: { player1: 8, player2: 11 },
                pair2: { player1: 9, player2: 10 },
              },
            ],
          },
          // Round 3
          {
            roundNumber: 3,
            courts: [
              {
                courtNumber: 1,
                pair1: { player1: 1, player2: 8 },
                pair2: { player1: 2, player2: 3 },
              },
              {
                courtNumber: 2,
                pair1: { player1: 4, player2: 12 },
                pair2: { player1: 5, player2: 11 },
              },
              {
                courtNumber: 3,
                pair1: { player1: 6, player2: 10 },
                pair2: { player1: 7, player2: 9 },
              },
            ],
          },
          // Round 4
          {
            roundNumber: 4,
            courts: [
              {
                courtNumber: 1,
                pair1: { player1: 1, player2: 11 },
                pair2: { player1: 5, player2: 7 },
              },
              {
                courtNumber: 2,
                pair1: { player1: 2, player2: 12 },
                pair2: { player1: 6, player2: 9 },
              },
              {
                courtNumber: 3,
                pair1: { player1: 3, player2: 4 },
                pair2: { player1: 8, player2: 10 },
              },
            ],
          },
        ];
        break;

      case 4: // 16 players, 4 courts
        pattern = [
          // Round 1
          {
            roundNumber: 1,
            courts: [
              {
                courtNumber: 1,
                pair1: { player1: 1, player2: 9 },
                pair2: { player1: 7, player2: 10 },
              },
              {
                courtNumber: 2,
                pair1: { player1: 2, player2: 11 },
                pair2: { player1: 5, player2: 6 },
              },
              {
                courtNumber: 3,
                pair1: { player1: 3, player2: 15 },
                pair2: { player1: 12, player2: 14 },
              },
              {
                courtNumber: 4,
                pair1: { player1: 4, player2: 16 },
                pair2: { player1: 8, player2: 13 },
              },
            ],
          },
          // Round 2
          {
            roundNumber: 2,
            courts: [
              {
                courtNumber: 1,
                pair1: { player1: 1, player2: 12 },
                pair2: { player1: 4, player2: 14 },
              },
              {
                courtNumber: 2,
                pair1: { player1: 2, player2: 8 },
                pair2: { player1: 10, player2: 11 },
              },
              {
                courtNumber: 3,
                pair1: { player1: 3, player2: 7 },
                pair2: { player1: 6, player2: 15 },
              },
              {
                courtNumber: 4,
                pair1: { player1: 5, player2: 9 },
                pair2: { player1: 13, player2: 16 },
              },
            ],
          },
          // Round 3
          {
            roundNumber: 3,
            courts: [
              {
                courtNumber: 1,
                pair1: { player1: 1, player2: 16 },
                pair2: { player1: 2, player2: 13 },
              },
              {
                courtNumber: 2,
                pair1: { player1: 3, player2: 10 },
                pair2: { player1: 4, player2: 11 },
              },
              {
                courtNumber: 3,
                pair1: { player1: 5, player2: 14 },
                pair2: { player1: 7, player2: 15 },
              },
              {
                courtNumber: 4,
                pair1: { player1: 6, player2: 9 },
                pair2: { player1: 8, player2: 12 },
              },
            ],
          },
          // Round 4
          {
            roundNumber: 4,
            courts: [
              {
                courtNumber: 1,
                pair1: { player1: 1, player2: 2 },
                pair2: { player1: 3, player2: 9 },
              },
              {
                courtNumber: 2,
                pair1: { player1: 4, player2: 8 },
                pair2: { player1: 5, player2: 15 },
              },
              {
                courtNumber: 3,
                pair1: { player1: 6, player2: 11 },
                pair2: { player1: 13, player2: 14 },
              },
              {
                courtNumber: 4,
                pair1: { player1: 7, player2: 12 },
                pair2: { player1: 10, player2: 16 },
              },
            ],
          },
        ];
        break;

      case 5: // 20 players, 5 courts
        pattern = [
          // Round 1
          {
            roundNumber: 1,
            courts: [
              {
                courtNumber: 1,
                pair1: { player1: 1, player2: 15 },
                pair2: { player1: 2, player2: 13 },
              },
              {
                courtNumber: 2,
                pair1: { player1: 3, player2: 17 },
                pair2: { player1: 8, player2: 10 },
              },
              {
                courtNumber: 3,
                pair1: { player1: 4, player2: 16 },
                pair2: { player1: 5, player2: 19 },
              },
              {
                courtNumber: 4,
                pair1: { player1: 6, player2: 18 },
                pair2: { player1: 7, player2: 9 },
              },
              {
                courtNumber: 5,
                pair1: { player1: 11, player2: 14 },
                pair2: { player1: 12, player2: 20 },
              },
            ],
          },
          // Round 2
          {
            roundNumber: 2,
            courts: [
              {
                courtNumber: 1,
                pair1: { player1: 1, player2: 20 },
                pair2: { player1: 4, player2: 5 },
              },
              {
                courtNumber: 2,
                pair1: { player1: 2, player2: 18 },
                pair2: { player1: 14, player2: 17 },
              },
              {
                courtNumber: 3,
                pair1: { player1: 3, player2: 7 },
                pair2: { player1: 9, player2: 15 },
              },
              {
                courtNumber: 4,
                pair1: { player1: 6, player2: 11 },
                pair2: { player1: 10, player2: 19 },
              },
              {
                courtNumber: 5,
                pair1: { player1: 8, player2: 16 },
                pair2: { player1: 12, player2: 13 },
              },
            ],
          },
          // Round 3
          {
            roundNumber: 3,
            courts: [
              {
                courtNumber: 1,
                pair1: { player1: 1, player2: 17 },
                pair2: { player1: 3, player2: 14 },
              },
              {
                courtNumber: 2,
                pair1: { player1: 2, player2: 5 },
                pair2: { player1: 11, player2: 18 },
              },
              {
                courtNumber: 3,
                pair1: { player1: 4, player2: 13 },
                pair2: { player1: 6, player2: 10 },
              },
              {
                courtNumber: 4,
                pair1: { player1: 7, player2: 15 },
                pair2: { player1: 8, player2: 12 },
              },
              {
                courtNumber: 5,
                pair1: { player1: 9, player2: 20 },
                pair2: { player1: 16, player2: 19 },
              },
            ],
          },
          // Round 4
          {
            roundNumber: 4,
            courts: [
              {
                courtNumber: 1,
                pair1: { player1: 1, player2: 7 },
                pair2: { player1: 11, player2: 12 },
              },
              {
                courtNumber: 2,
                pair1: { player1: 2, player2: 15 },
                pair2: { player1: 4, player2: 9 },
              },
              {
                courtNumber: 3,
                pair1: { player1: 3, player2: 18 },
                pair2: { player1: 5, player2: 6 },
              },
              {
                courtNumber: 4,
                pair1: { player1: 8, player2: 17 },
                pair2: { player1: 16, player2: 20 },
              },
              {
                courtNumber: 5,
                pair1: { player1: 10, player2: 13 },
                pair2: { player1: 14, player2: 19 },
              },
            ],
          },
        ];
        break;

      default:
        throw new Error(`Unsupported player count: ${playerCount}`);
    }

    const generatedMatches: HistoricalMatch[] = [];
    pattern.forEach((round) => {
      round.courts.forEach((court: any) => {
        generatedMatches.push({
          roundNumber: round.roundNumber,
          courtNumber: court.courtNumber,
          pair1: court.pair1,
          pair2: court.pair2,
          score: {
            pair1Games: 0,
            pair2Games: 0,
            isCompleted: false,
            winnerPair: null,
          },
          maxGames: courtsPerRound === 1 ? 9 : round.roundNumber === 4 ? 9 : 7,
        });
      });
    });

    return generatedMatches;
  };

  // Update player assignments when player count changes
  useEffect(() => {
    setPlayerAssignments(
      Array.from({ length: playerCount }, (_, i) => ({
        playerId: "",
        playerName: "",
        assignedNumber: i + 1,
      }))
    );
  }, [playerCount]);

  useEffect(() => {
    if (open) {
      setMatches(generateMatches());
    }
  }, [open, playerCount]);

  const handlePlayerAssignment = (position: number, playerId: string) => {
    const player = players.find((p) => p.id === playerId);
    if (!player) return;

    setPlayerAssignments((prev) =>
      prev.map((assignment) =>
        assignment.assignedNumber === position
          ? { ...assignment, playerId, playerName: player.name }
          : assignment
      )
    );
  };

  const getPlayerName = (playerNumber: number): string => {
    const assignment = playerAssignments.find(
      (a) => a.assignedNumber === playerNumber
    );
    return assignment?.playerName || `P${playerNumber}`;
  };

  const handlePairingChange = (
    matchIndex: number,
    pair: "pair1" | "pair2",
    position: "player1" | "player2",
    value: number
  ) => {
    setMatches((prev) =>
      prev.map((match, index) => {
        if (index === matchIndex) {
          return {
            ...match,
            [pair]: {
              ...match[pair],
              [position]: value,
            },
          };
        }
        return match;
      })
    );
  };

  const handleScoreChange = (
    matchIndex: number,
    field: "pair1Games" | "pair2Games",
    value: number
  ) => {
    setMatches((prev) =>
      prev.map((match, index) => {
        if (index === matchIndex) {
          const updatedScore = { ...match.score, [field]: value };
          const winningScore = Math.ceil(match.maxGames / 2);

          // Check if match is completed
          if (
            updatedScore.pair1Games >= winningScore ||
            updatedScore.pair2Games >= winningScore
          ) {
            updatedScore.isCompleted = true;
            updatedScore.winnerPair =
              updatedScore.pair1Games > updatedScore.pair2Games ? 1 : 2;
          } else {
            updatedScore.isCompleted = false;
            updatedScore.winnerPair = null;
          }

          return { ...match, score: updatedScore };
        }
        return match;
      })
    );
  };

  const handleNext = () => {
    if (activeStep === 0) {
      // Validate player assignments
      const allAssigned = playerAssignments.every((a) => a.playerId !== "");
      const uniqueAssignments =
        new Set(playerAssignments.map((a) => a.playerId)).size === playerCount;

      if (!allAssigned || !uniqueAssignments) {
        setError(
          `Please assign all ${playerCount} positions with unique players`
        );
        return;
      }
    }

    setError("");
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSave = () => {
    // Validate all matches have scores
    const allMatchesCompleted = matches.every((m) => m.score.isCompleted);
    if (!allMatchesCompleted) {
      setError("Please complete all match scores");
      return;
    }

    const courtCount = playerCount / 4; // Calculate courts from player count

    onSave({
      date: selectedDate,
      courtCount,
      playerAssignments: playerAssignments.filter((a) => a.playerId !== ""),
      matches,
    });
  };

  const getRoundMatches = (roundNumber: number) => {
    return matches.filter((m) => m.roundNumber === roundNumber);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <HistoryIcon />
          Add Historical League Day
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} orientation="vertical">
          {/* Step 1: Date and Player Assignment */}
          <Step>
            <StepLabel>Date & Player Assignment</StepLabel>
            <StepContent>
              <Box mb={3}>
                <TextField
                  label="League Day Date"
                  type="date"
                  value={selectedDate.toISOString().split("T")[0]}
                  onChange={(e) => setSelectedDate(new Date(e.target.value))}
                  fullWidth
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                />

                <FormControl fullWidth margin="normal">
                  <InputLabel>Number of Players</InputLabel>
                  <Select
                    value={playerCount}
                    label="Number of Players"
                    onChange={(e) =>
                      setPlayerCount(e.target.value as 4 | 8 | 12 | 16 | 20)
                    }
                  >
                    <MenuItem value={4}>4 Players (1 Court)</MenuItem>
                    <MenuItem value={8}>8 Players (2 Courts)</MenuItem>
                    <MenuItem value={12}>12 Players (3 Courts)</MenuItem>
                    <MenuItem value={16}>16 Players (4 Courts)</MenuItem>
                    <MenuItem value={20}>20 Players (5 Courts)</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Typography variant="h6" gutterBottom>
                Assign Players to Positions (P1 - P{playerCount})
              </Typography>

              <Grid container spacing={2}>
                {playerAssignments.map((assignment) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    key={assignment.assignedNumber}
                  >
                    <FormControl fullWidth>
                      <InputLabel>
                        Player {assignment.assignedNumber}
                      </InputLabel>
                      <Select
                        value={assignment.playerId}
                        label={`Player ${assignment.assignedNumber}`}
                        onChange={(e) =>
                          handlePlayerAssignment(
                            assignment.assignedNumber,
                            e.target.value
                          )
                        }
                      >
                        <MenuItem value="">
                          <em>Select Player</em>
                        </MenuItem>
                        {players
                          .filter((p) => p.isActive)
                          .filter(
                            (p) =>
                              !playerAssignments.find(
                                (a) =>
                                  a.playerId === p.id &&
                                  a.assignedNumber !== assignment.assignedNumber
                              )
                          )
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((player) => (
                            <MenuItem key={player.id} value={player.id}>
                              {player.name}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Grid>
                ))}
              </Grid>

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}

              <Box sx={{ mb: 2, mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  sx={{ mt: 1, mr: 1 }}
                >
                  Continue to Scores
                </Button>
              </Box>
            </StepContent>
          </Step>

          {/* Step 2: Edit Pairings (Optional) */}
          <Step>
            <StepLabel
              optional={
                <Typography variant="caption">
                  Optional - Edit if needed
                </Typography>
              }
            >
              Review & Edit Match Pairings
            </StepLabel>
            <StepContent>
              <Typography
                variant="body2"
                color="text.secondary"
                gutterBottom
                sx={{ mb: 2 }}
              >
                Review the generated pairings below. You can edit them if the
                historical matches had different pairings.
              </Typography>

              <Tabs
                value={currentRound}
                onChange={(_, newValue) => setCurrentRound(newValue)}
              >
                {Array.from({ length: maxRounds }, (_, i) => i + 1).map(
                  (roundNum) => (
                    <Tab key={roundNum} label={`Round ${roundNum}`} />
                  )
                )}
              </Tabs>

              {Array.from({ length: maxRounds }, (_, i) => i + 1).map(
                (roundNum, index) => (
                  <TabPanel key={roundNum} value={currentRound} index={index}>
                    <Grid container spacing={2}>
                      {getRoundMatches(roundNum).map((match) => {
                        const globalMatchIndex = matches.findIndex(
                          (m) =>
                            m.roundNumber === match.roundNumber &&
                            m.courtNumber === match.courtNumber
                        );

                        return (
                          <Grid
                            item
                            xs={12}
                            md={6}
                            key={`${match.roundNumber}-${match.courtNumber}`}
                          >
                            <Card>
                              <CardContent>
                                <Typography
                                  variant="h6"
                                  color="primary"
                                  gutterBottom
                                >
                                  Court {match.courtNumber}
                                </Typography>

                                {/* Pair 1 */}
                                <Box mb={2}>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    gutterBottom
                                  >
                                    Pair 1
                                  </Typography>
                                  <Box display="flex" gap={1}>
                                    <FormControl fullWidth size="small">
                                      <InputLabel>Player 1</InputLabel>
                                      <Select
                                        value={match.pair1.player1}
                                        label="Player 1"
                                        onChange={(e) =>
                                          handlePairingChange(
                                            globalMatchIndex,
                                            "pair1",
                                            "player1",
                                            Number(e.target.value)
                                          )
                                        }
                                      >
                                        {Array.from(
                                          { length: playerCount },
                                          (_, i) => i + 1
                                        )
                                          .sort((a, b) =>
                                            getPlayerName(a).localeCompare(
                                              getPlayerName(b)
                                            )
                                          )
                                          .map((num) => (
                                            <MenuItem key={num} value={num}>
                                              P{num} - {getPlayerName(num)}
                                            </MenuItem>
                                          ))}
                                      </Select>
                                    </FormControl>
                                    <FormControl fullWidth size="small">
                                      <InputLabel>Player 2</InputLabel>
                                      <Select
                                        value={match.pair1.player2}
                                        label="Player 2"
                                        onChange={(e) =>
                                          handlePairingChange(
                                            globalMatchIndex,
                                            "pair1",
                                            "player2",
                                            Number(e.target.value)
                                          )
                                        }
                                      >
                                        {Array.from(
                                          { length: playerCount },
                                          (_, i) => i + 1
                                        )
                                          .sort((a, b) =>
                                            getPlayerName(a).localeCompare(
                                              getPlayerName(b)
                                            )
                                          )
                                          .map((num) => (
                                            <MenuItem key={num} value={num}>
                                              P{num} - {getPlayerName(num)}
                                            </MenuItem>
                                          ))}
                                      </Select>
                                    </FormControl>
                                  </Box>
                                </Box>

                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  align="center"
                                  gutterBottom
                                >
                                  VS
                                </Typography>

                                {/* Pair 2 */}
                                <Box>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    gutterBottom
                                  >
                                    Pair 2
                                  </Typography>
                                  <Box display="flex" gap={1}>
                                    <FormControl fullWidth size="small">
                                      <InputLabel>Player 1</InputLabel>
                                      <Select
                                        value={match.pair2.player1}
                                        label="Player 1"
                                        onChange={(e) =>
                                          handlePairingChange(
                                            globalMatchIndex,
                                            "pair2",
                                            "player1",
                                            Number(e.target.value)
                                          )
                                        }
                                      >
                                        {Array.from(
                                          { length: playerCount },
                                          (_, i) => i + 1
                                        )
                                          .sort((a, b) =>
                                            getPlayerName(a).localeCompare(
                                              getPlayerName(b)
                                            )
                                          )
                                          .map((num) => (
                                            <MenuItem key={num} value={num}>
                                              P{num} - {getPlayerName(num)}
                                            </MenuItem>
                                          ))}
                                      </Select>
                                    </FormControl>
                                    <FormControl fullWidth size="small">
                                      <InputLabel>Player 2</InputLabel>
                                      <Select
                                        value={match.pair2.player2}
                                        label="Player 2"
                                        onChange={(e) =>
                                          handlePairingChange(
                                            globalMatchIndex,
                                            "pair2",
                                            "player2",
                                            Number(e.target.value)
                                          )
                                        }
                                      >
                                        {Array.from(
                                          { length: playerCount },
                                          (_, i) => i + 1
                                        )
                                          .sort((a, b) =>
                                            getPlayerName(a).localeCompare(
                                              getPlayerName(b)
                                            )
                                          )
                                          .map((num) => (
                                            <MenuItem key={num} value={num}>
                                              P{num} - {getPlayerName(num)}
                                            </MenuItem>
                                          ))}
                                      </Select>
                                    </FormControl>
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </TabPanel>
                )
              )}

              <Box sx={{ mb: 2, mt: 2 }}>
                <Button onClick={handleBack} sx={{ mt: 1, mr: 1 }}>
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  sx={{ mt: 1, mr: 1 }}
                >
                  Continue to Scores
                </Button>
              </Box>
            </StepContent>
          </Step>

          {/* Step 3: Score Entry */}
          <Step>
            <StepLabel>Enter Match Scores</StepLabel>
            <StepContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Enter the final scores for all matches across {maxRounds}{" "}
                rounds.{" "}
                {maxRounds === 3
                  ? "All rounds are best of 9 (first to 5)."
                  : "Rounds 1-3 are best of 7 (first to 4), Round 4 is best of 9 (first to 5)."}
              </Typography>

              <Tabs
                value={currentRound}
                onChange={(_, newValue) => setCurrentRound(newValue)}
              >
                {Array.from({ length: maxRounds }, (_, i) => i + 1).map(
                  (roundNum) => (
                    <Tab
                      key={roundNum}
                      label={`Round ${roundNum}${
                        maxRounds === 3 || roundNum === 4
                          ? " (Best of 9)"
                          : " (Best of 7)"
                      }`}
                    />
                  )
                )}
              </Tabs>

              {Array.from({ length: maxRounds }, (_, i) => i + 1).map(
                (roundNum, index) => (
                  <TabPanel key={roundNum} value={currentRound} index={index}>
                    <Grid container spacing={2}>
                      {getRoundMatches(roundNum).map((match, matchIndex) => {
                        const globalMatchIndex = matches.findIndex(
                          (m) =>
                            m.roundNumber === match.roundNumber &&
                            m.courtNumber === match.courtNumber
                        );
                        const winningScore = Math.ceil(match.maxGames / 2);

                        return (
                          <Grid
                            item
                            xs={12}
                            md={4}
                            key={`${match.roundNumber}-${match.courtNumber}`}
                          >
                            <Card>
                              <CardContent>
                                <Typography
                                  variant="h6"
                                  color="primary"
                                  gutterBottom
                                >
                                  Court {match.courtNumber}
                                </Typography>

                                {/* Team 1 */}
                                <Box mb={1}>
                                  <Typography variant="body2" fontWeight="bold">
                                    {getPlayerName(match.pair1.player1)} &{" "}
                                    {getPlayerName(match.pair1.player2)}
                                  </Typography>
                                  <TextField
                                    type="number"
                                    label="Games Won"
                                    value={match.score.pair1Games}
                                    onChange={(e) =>
                                      handleScoreChange(
                                        globalMatchIndex,
                                        "pair1Games",
                                        Math.max(
                                          0,
                                          parseInt(e.target.value) || 0
                                        )
                                      )
                                    }
                                    inputProps={{ min: 0, max: winningScore }}
                                    size="small"
                                    fullWidth
                                    sx={{ mt: 0.5 }}
                                  />
                                </Box>

                                <Divider sx={{ my: 1 }} />

                                {/* Team 2 */}
                                <Box mb={1}>
                                  <Typography variant="body2" fontWeight="bold">
                                    {getPlayerName(match.pair2.player1)} &{" "}
                                    {getPlayerName(match.pair2.player2)}
                                  </Typography>
                                  <TextField
                                    type="number"
                                    label="Games Won"
                                    value={match.score.pair2Games}
                                    onChange={(e) =>
                                      handleScoreChange(
                                        globalMatchIndex,
                                        "pair2Games",
                                        Math.max(
                                          0,
                                          parseInt(e.target.value) || 0
                                        )
                                      )
                                    }
                                    inputProps={{ min: 0, max: winningScore }}
                                    size="small"
                                    fullWidth
                                    sx={{ mt: 0.5 }}
                                  />
                                </Box>

                                {match.score.isCompleted && (
                                  <Alert severity="success" sx={{ mt: 1 }}>
                                    Winner: Team {match.score.winnerPair}
                                  </Alert>
                                )}
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </TabPanel>
                )
              )}

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}

              <Box sx={{ mb: 2, mt: 2 }}>
                <Button onClick={handleBack} sx={{ mt: 1, mr: 1 }}>
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  startIcon={<SaveIcon />}
                  sx={{ mt: 1, mr: 1 }}
                >
                  Save Historical League Day
                </Button>
              </Box>
            </StepContent>
          </Step>
        </Stepper>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}
